import React from 'react';
import { useParams } from 'react-router-dom';
import { format } from 'date-fns';
import {
    alpha,
    Box,
    Button,
    Chip,
    Divider,
    Grid,
    Paper,
    Skeleton,
    styled,
    Tooltip,
    Typography
} from '@mui/material';
import AdsIcon from '@mui/icons-material/AdsClick';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import QrCodeIcon from '@mui/icons-material/QrCode';
import { useSnackbar } from 'notistack';
import LockIcon from '@mui/icons-material/Lock';

import { MainLayout } from 'layouts/MainLayout';
import { CAMPAIGNS_TRACKING_LINKS } from 'config/routes';
import { Throbber } from 'ui/Throbber';
import { StatusCell } from 'ui/table/StatusCell';
import { useTrackingLinksView } from 'components/tracking-links/hooks/useTrackingLinksView';
import { DownloadQRModal } from 'components/tracking-links/components/DownloadQRModal';
import { buildShortLink } from 'components/tracking-links/helpers';
import {
    CustomParamKeys,
    DeepLinkDestinationsLabels,
    getDeepLinkDestinationFromCustomPath,
    TrackingLinkType,
    TrackingLinkTypeLabels
} from 'components/tracking-links/trackingLinks';
import { copyToClipboard } from 'lib/helpers';
import { useAppSelector } from 'store/hooks';
import { UnlockPremiumUTMBanner } from 'components/tracking-links/components/UnlockPremiumUTMBanner';
import { CopyLink } from 'ui/CopyLink';
import { UTMDetails } from 'components/tracking-links/components/UTMDetails';

const CardHeader = styled(Box)(({ theme }) => ({
    padding: theme.spacing(1),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    height: 46,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
}));

const MonoText = styled(Typography)({
    fontFamily: 'monospace',
    wordBreak: 'break-all'
});

const PremimuChip = styled(Chip)(({ theme }) => ({
    backgroundColor: alpha(theme.palette.purple.core, 0.1),
    borderColor: alpha(theme.palette.purple.core, 0.25),
    color: theme.palette.purple.core,
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    fontSize: '0.75rem',
    '.MuiChip-icon': {
        fontSize: '0.75rem',
        color: theme.palette.purple.core
    }
}));

const EM_DASH = '—';
const CLICKS_DELAY_TOOLTIP = `Click stats can take up to 2 hours to appear ${EM_DASH} check back soon if you're not seeing any yet.`;

interface FieldProps {
    label: React.ReactNode;
    children: React.ReactNode;
}

const Field = ({ label, children }: FieldProps) => (
    <Box display="flex" flexDirection="column" gap={0.5}>
        <Typography variant="caption" color="text.secondary">
            {label}
        </Typography>
        <Box>{children}</Box>
    </Box>
);

export function TrackingLinksViewPage() {
    const { linkId } = useParams<{ linkId: string }>();
    const { enqueueSnackbar } = useSnackbar();
    const { trackingLink, clicks, isClicksLoading } = useTrackingLinksView({ linkId });
    const { settings } = useAppSelector(state => state.settings);
    const { locations } = useAppSelector(state => state.locations);

    const utmParametersEnabled = React.useMemo(
        () => settings?.links?.utmParametersEnabled ?? false,
        [settings?.links?.utmParametersEnabled]
    );

    const [downloadQRModalOpen, setDownloadQRModalOpen] = React.useState(false);

    const locationTitle = React.useMemo(() => {
        const locationId = trackingLink?.customPath?.split('/')[0];
        if (!locationId) {
            return EM_DASH;
        }
        const match = locations?.find(
            location => location._id === locationId || location.title === locationId
        );
        return match?.title || locationId;
    }, [locations, trackingLink?.customPath]);

    const tableNumber = React.useMemo(() => {
        const tableNumber = trackingLink?.customQueryParameters?.find(
            param => param.key === CustomParamKeys.TABLE_NUMBER
        )?.value;
        if (!tableNumber) {
            return EM_DASH;
        }
        return tableNumber;
    }, [trackingLink?.customQueryParameters]);

    const deepLinkDestination = React.useMemo(() => {
        const destination = getDeepLinkDestinationFromCustomPath(trackingLink?.customPath);
        if (!destination) {
            return EM_DASH;
        }
        return DeepLinkDestinationsLabels[destination] || destination;
    }, [trackingLink?.customPath]);

    const shortLinkUrl = React.useMemo(
        () =>
            trackingLink
                ? buildShortLink(import.meta.env.TRACKING_SHORT_LINK_URL, trackingLink.shortLinkId)
                : '',
        [trackingLink]
    );

    const handleOpenDownloadQRModal = React.useCallback(() => {
        setDownloadQRModalOpen(true);
    }, []);

    const handleCloseDownloadQRModal = React.useCallback(() => {
        setDownloadQRModalOpen(false);
    }, []);

    const handleCopyShortLink = React.useCallback(() => {
        if (!shortLinkUrl) {
            return;
        }
        copyToClipboard(shortLinkUrl);
        enqueueSnackbar('Link copied to clipboard', { variant: 'info' });
    }, [shortLinkUrl, enqueueSnackbar]);

    if (!trackingLink) {
        return (
            <MainLayout
                breadcrumbs={[{ label: 'Campaign Links', url: CAMPAIGNS_TRACKING_LINKS }]}
                pageName="Campaign Link Details"
                pageDescription="View more information on your campaign link"
            >
                <Throbber />
            </MainLayout>
        );
    }

    const isWebLink = trackingLink.type === TrackingLinkType.EXTERNAL_LINK;
    const isAppLink = trackingLink.type === TrackingLinkType.APP_LINK;

    return (
        <MainLayout
            breadcrumbs={[{ label: 'Campaign Links', url: CAMPAIGNS_TRACKING_LINKS }]}
            pageName={trackingLink.internalTitle || 'Campaign Link Details'}
            pageDescription="View more information on your campaign link"
        >
            <Grid container spacing={2}>
                <Grid size={{ xs: 12, lg: 8 }}>
                    <Box display="flex" flexDirection="column" gap={2}>
                        <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
                            <CardHeader>
                                <Typography variant="h6">About</Typography>
                                <Button
                                    startIcon={<QrCodeIcon />}
                                    variant="outlined"
                                    size="small"
                                    color="primary"
                                    onClick={handleOpenDownloadQRModal}
                                    disabled={trackingLink.isArchived}
                                >
                                    Download QR
                                </Button>
                            </CardHeader>
                            <Divider />
                            <Box padding={2}>
                                <Grid container spacing={3}>
                                    <Grid size={6}>
                                        <Field label="Link name (internal)">
                                            <Typography variant="body1" fontWeight="medium">
                                                {trackingLink.internalTitle}
                                            </Typography>
                                        </Field>
                                    </Grid>
                                    <Grid size={6}>
                                        <Field label="Link type">
                                            <Typography variant="body1" fontWeight="medium">
                                                {TrackingLinkTypeLabels[trackingLink.type]}
                                            </Typography>
                                        </Field>
                                    </Grid>
                                    <Grid size={6}>
                                        <Field label="Status">
                                            <StatusCell
                                                value={trackingLink.isArchived ? 'false' : 'true'}
                                                labelMap={{ true: 'Active', false: 'Archived' }}
                                                colorMap={{ true: 'green', false: 'red' }}
                                                defaultColor="grey"
                                            />
                                        </Field>
                                    </Grid>
                                    <Grid size={6}>
                                        <Field label="Created">
                                            <Typography variant="body1" fontWeight="medium">
                                                {format(new Date(trackingLink.createdAt), 'dd/MM/yyyy')}
                                            </Typography>
                                        </Field>
                                    </Grid>
                                    <Grid size={6}>
                                        <Field
                                            label={
                                                <Box
                                                    component="span"
                                                    display="inline-flex"
                                                    alignItems="center"
                                                    gap={0.5}
                                                >
                                                    Total clicks
                                                    <Tooltip
                                                        title={CLICKS_DELAY_TOOLTIP}
                                                        placement="top"
                                                        arrow
                                                    >
                                                        <InfoOutlinedIcon
                                                            sx={{
                                                                fontSize: '0.75rem',
                                                                color: 'text.secondary',
                                                                cursor: 'help',
                                                                verticalAlign: 'middle'
                                                            }}
                                                        />
                                                    </Tooltip>
                                                </Box>
                                            }
                                        >
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <AdsIcon sx={{ fontSize: '1rem' }} color="action" />
                                                {isClicksLoading ? (
                                                    <Skeleton variant="text" width={60} height={24} />
                                                ) : (
                                                    <Typography variant="body1" fontWeight="medium">
                                                        {clicks}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Field>
                                    </Grid>
                                    {isWebLink && (
                                        <>
                                            <Grid size={6}>
                                                <Field label="Location">
                                                    <Typography variant="body1" fontWeight="medium">
                                                        {locationTitle}
                                                    </Typography>
                                                </Field>
                                            </Grid>
                                            <Grid size={6}>
                                                <Field label="Deep link destination">
                                                    <Typography variant="body1" fontWeight="medium">
                                                        {deepLinkDestination}
                                                    </Typography>
                                                </Field>
                                            </Grid>
                                            <Grid size={6}>
                                                <Field label="Table number">
                                                    {!utmParametersEnabled ? (
                                                        <PremimuChip
                                                            icon={<LockIcon sx={{ fontSize: '0.625rem' }} />}
                                                            size="small"
                                                            label="Premium"
                                                            variant="outlined"
                                                        />
                                                    ) : (
                                                        <Typography variant="body1" fontWeight="medium">
                                                            {tableNumber}
                                                        </Typography>
                                                    )}
                                                </Field>
                                            </Grid>
                                        </>
                                    )}
                                    {isAppLink && (
                                        <Grid size={6}>
                                            <Field label="Desktop fallback URL">
                                                <MonoText variant="body2">
                                                    {trackingLink.fallbackUrl}
                                                </MonoText>
                                            </Field>
                                        </Grid>
                                    )}
                                </Grid>
                                <Divider sx={{ my: 3 }} />
                                <Grid container spacing={3}>
                                    <Grid size={6}>
                                        <Field label="Short link">
                                            <CopyLink
                                                link={shortLinkUrl}
                                                onCopy={handleCopyShortLink}
                                                disabled={trackingLink.isArchived}
                                            />
                                        </Field>
                                    </Grid>
                                </Grid>
                            </Box>
                        </Paper>
                        {!utmParametersEnabled && <UnlockPremiumUTMBanner direction="column" />}
                        {utmParametersEnabled && <UTMDetails trackingLink={trackingLink} />}
                    </Box>
                </Grid>
            </Grid>
            <DownloadQRModal
                open={downloadQRModalOpen}
                link={trackingLink}
                shortLinkUrl={shortLinkUrl}
                onClose={handleCloseDownloadQRModal}
            />
        </MainLayout>
    );
}
